import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Scale, ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const DEMO = [
  ["Registry / Administrator", "registry@resolveos.demo"], ["VCAT Member", "member@resolveos.demo"], ["Applicant lawyer", "applicant@resolveos.demo"],
  ["Respondent lawyer", "respondent@resolveos.demo"], ["Mediator / ADR Officer", "mediator@resolveos.demo"],
];

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("registry@resolveos.demo");
  const [password, setPassword] = useState("Demo1234!");
  const [step, setStep] = useState(1);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(false);
  const DEVICE_KEY = "resolveos_device";

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password, device_token: localStorage.getItem(DEVICE_KEY) || null });
      if (data.trusted_device) { login(data.access_token, data.user); nav(data.enrolment_required ? "/security" : "/"); return; }
      setChallenge(data); setCode(data.demo_code || ""); setStep(2);
    } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  const verify = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", { challenge_id: challenge.challenge_id, code, remember_device: remember, device_label: navigator.userAgent.slice(0, 120) });
      if (data.device_token) localStorage.setItem(DEVICE_KEY, data.device_token); else if (!remember) localStorage.removeItem(DEVICE_KEY);
      login(data.access_token, data.user); nav(data.enrolment_required ? "/security" : "/");
    } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12 relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1607778914885-787b06728b07?crop=entropy&cs=srgb&fm=jpg&q=60&w=1600" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative flex items-center gap-3"><div className="h-9 w-9 rounded bg-white text-slate-900 flex items-center justify-center"><Scale className="h-5 w-5" /></div><span className="text-lg font-bold">ResolveOS</span></div>
        <div className="relative max-w-lg">
          <div className="eyebrow !text-slate-300">Tribunal case management</div>
          <h1 className="text-4xl sm:text-5xl font-bold mt-3 leading-tight">AI before the judge, not AI as the judge.</h1>
          <p className="text-slate-300 mt-5 text-base leading-relaxed">ResolveOS automates registry administration, structures evidence and surfaces procedural issues — and escalates every genuine decision to an authorised human.</p>
        </div>
        <div className="relative text-xs text-slate-400 mono">VCAT Built Environment · Domestic Building Disputes · Rule set v1.0.0</div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <h2 className="text-2xl font-semibold text-slate-900">{step === 1 ? "Sign in" : "Multi-factor verification"}</h2>
          <p className="text-sm text-slate-500 mt-1">{step === 1 ? "Role-based access · MFA enforced for all roles" : challenge?.delivery === "totp" ? "Enter the 6-digit code from your authenticator app, or a backup code" : `A 6-digit code was sent to ${challenge?.masked_phone} (simulated for demo)`}</p>
          {step === 1 ? (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
              <div><Label htmlFor="email">Email</Label><Input id="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" /></div>
              <div><Label htmlFor="password">Password</Label><Input id="password" type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" /></div>
              {err && <div className="text-sm text-rose-700" data-testid="login-error">{err}</div>}
              <Button type="submit" className="w-full bg-blue-900 hover:bg-blue-950" disabled={busy} data-testid="login-submit">{busy ? "Checking…" : "Continue"}</Button>
            </form>
          ) : (
            <form onSubmit={verify} className="mt-6 space-y-4" data-testid="mfa-form">
              {challenge?.delivery === "totp" ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 flex items-start gap-2" data-testid="mfa-totp-notice">
                  <ShieldCheck className="h-4 w-4 mt-0.5" /><div>Authenticator app enabled.<div className="text-xs mt-0.5">{challenge.backup_codes_remaining} backup codes remaining.</div></div>
                </div>
              ) : (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 mt-0.5" /><div>Demo MFA code: <span className="mono font-semibold" data-testid="mfa-demo-code">{challenge?.demo_code}</span><div className="text-xs mt-0.5">Enrol an authenticator app under Security after signing in to use real TOTP codes.</div></div>
              </div>)}
              <div><Label htmlFor="code">Verification code</Label><Input id="code" data-testid="mfa-code-input" className="mono text-lg tracking-[0.3em]" value={code} onChange={(e) => setCode(e.target.value)} maxLength={9} autoComplete="one-time-code" /></div>
              {err && <div className="text-sm text-rose-700" data-testid="mfa-error">{err}</div>}
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"><input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="mfa-remember-device" />Remember this device for 30 days (skip the code on this browser)</label>
              <Button type="submit" className="w-full bg-blue-900 hover:bg-blue-950" disabled={busy} data-testid="mfa-submit">{busy ? "Verifying…" : "Verify and sign in"}</Button>
              <button type="button" className="text-xs text-slate-500 underline" onClick={() => setStep(1)} data-testid="mfa-back">Back</button>
            </form>
          )}
          <div className="mt-8 space-y-1">
            <Link to="/register" className="block text-sm text-blue-900 font-medium underline" data-testid="login-register-link">New applicant or respondent? Register with identity verification</Link>
            <Link to="/verify-receipt" className="text-xs text-blue-900 underline" data-testid="login-verify-receipt-link">Verify a lodgement receipt (no sign-in needed)</Link>
          </div>
          <div className="mt-6">
            <div className="eyebrow mb-2">Demo accounts · password Demo1234!</div>
            <div className="grid grid-cols-1 gap-1">
              {DEMO.map(([l, e]) => (
                <button key={e} type="button" data-testid={`demo-account-${e.split("@")[0]}`} onClick={() => { setEmail(e); setStep(1); }} className="text-left text-sm px-3 py-1.5 rounded border border-slate-200 hover:border-blue-300 hover:bg-blue-50 flex justify-between">
                  <span className="text-slate-800">{l}</span><span className="mono text-xs text-slate-500">{e}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
